import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {useWorkspaceEventContainerTemplates} from "~/contexts/workspaceContext";
import type {
    EventContainerTemplate,
    EventContainerTemplateConfiguration,
} from "~/domainModels/workspaceModules/eventContainersModule";

export function getOptionalEventContainerTemplate(
    eventContainerTemplates: ReadonlyArray<EventContainerTemplate>,
    templateId: Uuid,
): EventContainerTemplate | null {
    return eventContainerTemplates.find((template) => template.id === templateId) ?? null;
}

export function getRequiredEventContainerTemplate(
    eventContainerTemplates: ReadonlyArray<EventContainerTemplate>,
    templateId: Uuid,
): EventContainerTemplate {
    const template = getOptionalEventContainerTemplate(eventContainerTemplates, templateId);
    if (template === null) {
        throw new Error(`Active Event Container Template not found for id ${templateId}`);
    }
    return template;
}

export function useEventContainerTemplates(): Array<EventContainerTemplate> {
    return useWorkspaceEventContainerTemplates();
}

export function useOnlyEventContainerTemplate(): EventContainerTemplate {
    const eventContainerTemplates = useEventContainerTemplates();
    if (eventContainerTemplates.length !== 1) {
        throw new Error(`Expected exactly one active Event Container Template, found ${eventContainerTemplates.length}`);
    }
    return eventContainerTemplates[0]!;
}

export function useOptionalEventContainerTemplate(templateId: Uuid): EventContainerTemplate | null {
    return getOptionalEventContainerTemplate(useEventContainerTemplates(), templateId);
}

export function useRequiredEventContainerTemplate(templateId: Uuid): EventContainerTemplate {
    return getRequiredEventContainerTemplate(useEventContainerTemplates(), templateId);
}

export function useOptionalEventContainerTemplateConfiguration(
    templateId: Uuid,
): EventContainerTemplateConfiguration | null {
    return useOptionalEventContainerTemplate(templateId)?.configuration ?? null;
}

export function useRequiredEventContainerTemplateConfiguration(
    templateId: Uuid,
): EventContainerTemplateConfiguration {
    return useRequiredEventContainerTemplate(templateId).configuration;
}
